import { HttpClient, HttpResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Endpoints } from '@shared/Endpoints';
import { TAccountRegion, TRegionFiles } from '@shared/interfaces/TProject';
import { catchError, map, Observable, of, switchMap, tap, zip } from 'rxjs';
import { LoadingService, TLoadingItem } from 'src/app/services/loading.service';
import { environment } from 'src/environments/environment';
import { TBaseSettingsTabParams } from '../project-settings.component';

@Component({
  selector: 'app-regions-files',
  templateUrl: './regions-files.component.html',
  styleUrls: ['./regions-files.component.scss']
})
export class RegionsFilesComponent implements OnInit {
    public newRegionData: TAccountRegion = {
    id: '',
    name: '',
    description: '',
    budgets: [],
  };
  public params: TBaseSettingsTabParams;

  constructor(
    public dialogRef: MatDialogRef<RegionsFilesComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TBaseSettingsTabParams,
    private _loadingService: LoadingService,
    private _http: HttpClient,
  ) {
    this.params = data;
    console.log('RegionsFilesComponent initialized with params:', this.params);

    // files
    // Pharmacy data
    // Brick data
  }

  ngOnInit(): void {}

  public getRegions(): TAccountRegion[] {
    return this.params.selectedProject?.accountRegions ?? [];
  }

  public getRegionFiles(regionId: string): TRegionFiles {
    return this.params.selectedProject?.regionFiles?.find(files => files.regionId === regionId)
    || { regionId, pharmacyDataUrl: '', brickDataUrl: '' } as TRegionFiles;
  }

  addFile(key: string, regionId: string, event: Event): void {
    if (this.params.selectedProject) {
      if (!this.params.selectedProject.regionFiles) { this.params.selectedProject.regionFiles = [] }
      if (this.params.selectedProject.regionFiles) {
        const regionFiles = this.getRegionFiles(regionId);
        if (key === 'pharmacy') key = 'pharmacyDataUrl';
        else if (key === 'brick') key = 'brickDataUrl';
        // upload File
        const target: HTMLInputElement = event.target as HTMLInputElement;
        const files: FileList | null = target.files;
        const xls: File = files![0];
        if (xls.size === 0) {
          this._loadingService.triggerErrorMessage('Invalid file');
        } else if (xls.size > 5242880) {
          this._loadingService.triggerErrorMessage('File to large (max 5 MB)');
        } else if (xls.type !== 'application/vnd.ms-excel' && xls.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
          this._loadingService.triggerErrorMessage('Invalid file type, only Excel files allowed');
        } else {
          this.uploadFileRequest$(xls).subscribe((url: string) => {
            if (url) {
              // add url to regionFiles
              regionFiles[key] = url;
              if (this.params.selectedProject) {
                this.params.selectedProject.regionFiles = [...this.params.selectedProject.regionFiles.filter(files => files.regionId !== regionId), regionFiles ];
              }
            }
          });
        }
      } else {
        this.params.selectedProject.regionFiles = [];
      }
    }
  }

  uploadFileRequest$(file: File): Observable<string> {
    const url: string = Endpoints.BUILD_URL('support', {
      base: environment.apiUrl,
      endpoint: 'upload',
      params: '',
    });
    const formData = new FormData();
    formData.append('file', file);
    return of([]).pipe(
      map(() => {
        // adding loadingItem inside a pipe prevents showing loading indicator before subscription
        const loadingItem: TLoadingItem = this._loadingService.buildLoadingItem('Uploading file...');
        this._loadingService.addLoadingItem(loadingItem);
        return loadingItem;
      }),
      switchMap((loadingItem) => {
        return zip(this._http.post<HttpResponse<string>>(url, formData), of(loadingItem));
      }),
      tap(([response, loadingItem]) => this._loadingService.removeItem(loadingItem)),
      map(([response, loadingItem]) => response.url ?? ''),
      catchError((err) => {
        console.error(err);
        this._loadingService.onError();
        return of(err);
      })
    );
  }

    deleteFile(key: string, regionId: string, url: string): void {
    if (this.params.selectedProject) {
      if (!this.params.selectedProject.regionFiles) { this.params.selectedProject.regionFiles = [] }
      if (this.params.selectedProject.regionFiles) {
        const regionFiles = this.getRegionFiles(regionId);
        if (key === 'pharmacy') key = 'pharmacyDataUrl';
        else if (key === 'brick') key = 'brickDataUrl';
        // delete File
        this.deleteFileRequest$(url).subscribe(() => {
          // remove url from regionFiles
          regionFiles[key] = '';
          if (this.params.selectedProject) {
            this.params.selectedProject.regionFiles = [...this.params.selectedProject.regionFiles.filter(files => files.regionId !== regionId), regionFiles ];
          }
        });
      } else {
        this.params.selectedProject.regionFiles = [];
      }
    }
  }

  deleteFileRequest$(fileUrl: string): Observable<any> {
    const bucketName: string = fileUrl.split('/')[3];
    const fileName: string = fileUrl.split(`/${bucketName}/`)[1];
    const url: string = Endpoints.BUILD_URL('support', {
      base: environment.apiUrl,
      endpoint: 'deleteDocument',
      params: '',
    });
    return of([]).pipe(
      map(() => {
        // adding loadingItem inside a pipe prevents showing loading indicator before subscription
        const loadingItem: TLoadingItem = this._loadingService.buildLoadingItem('Deleting file...');
        this._loadingService.addLoadingItem(loadingItem);
        return loadingItem;
      }),
      switchMap((loadingItem) => {
        return zip(this._http.delete(`${url}${fileName}`), of(loadingItem));
      }),
      tap(([req, loadingItem]) => {
        this._loadingService.removeItem(loadingItem);
      }),
      map(([req, loadingItem]) => req),
      catchError((err) => {
        console.error(err);
        this._loadingService.onError();
        return of(err);
      })
    );
  }
}
